import React, { createContext, useContext, useState, useEffect } from 'react';
import { auth } from '../firebase';
import {
  signInWithEmailAndPassword,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  sendSignInLinkToEmail,
  signInWithEmailLink,
  isSignInWithEmailLink,
} from 'firebase/auth';

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        try {
          // Force token refresh to get latest custom claims
          const tokenResult = await user.getIdTokenResult(true);
          const role = tokenResult.claims.role || null;

          setCurrentUser(user);
          setUserRole(role);
        } catch (error) {
          console.error('Error fetching user claims:', error);
          setCurrentUser(user);
          setUserRole(null);
        }
      } else {
        setCurrentUser(null);
        setUserRole(null);
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const signIn = async (email, password) => {
    const result = await signInWithEmailAndPassword(auth, email, password);

    // Get custom claims after sign in
    const tokenResult = await result.user.getIdTokenResult(true);
    const role = tokenResult.claims.role || null;
    setUserRole(role);

    return result;
  };

  const signOut = () => {
    return firebaseSignOut(auth);
  };

  const refreshUserRole = async () => {
    if (currentUser) {
      const tokenResult = await currentUser.getIdTokenResult(true);
      const role = tokenResult.claims.role || null;
      setUserRole(role);
    }
  };

  const sendEmailLink = async (email) => {
    const actionCodeSettings = {
      url: window.location.origin + '/builder',
      handleCodeInApp: true,
    };

    await sendSignInLinkToEmail(auth, email, actionCodeSettings);
    // Save email to localStorage to complete sign-in later
    window.localStorage.setItem('emailForSignIn', email);
  };

  const completeEmailLinkSignIn = async (email) => {
    if (isSignInWithEmailLink(auth, window.location.href)) {
      const result = await signInWithEmailLink(auth, email, window.location.href);
      // Clear email from localStorage
      window.localStorage.removeItem('emailForSignIn');

      // Get custom claims after sign in
      const tokenResult = await result.user.getIdTokenResult(true);
      const role = tokenResult.claims.role || null;
      setUserRole(role);

      return result;
    }
    throw new Error('Invalid sign-in link');
  };

  const checkEmailLink = () => {
    return isSignInWithEmailLink(auth, window.location.href);
  };

  const value = {
    currentUser,
    userRole,
    loading,
    signIn,
    signOut,
    refreshUserRole,
    sendEmailLink,
    completeEmailLinkSignIn,
    checkEmailLink,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}
